import { AddIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Heading, Spinner } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIntercom } from "react-use-intercom";
import type { SelectionCriterionDto, SourcingEventV2Dto } from "../../../../../autogen/bff-api";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import { QualificationRequirementDrawer, type QualificationFormValues } from "../components/QualificationRequirementDrawer";
import { QualificationRequirementList } from "../components/QualificationRequirementList";
import { useSaveField } from "../hooks/useSaveField";

// t('Delete requirement') t('Are you sure you want to delete this requirement?') t('Delete')

type DrawerMode =
  | { kind: "add" }
  | { kind: "edit"; index: number; initialValues: QualificationFormValues };

interface Props {
  eventId: string;
  event: SourcingEventV2Dto;
  previousStep: () => void;
  nextStep: () => void;
  isActive: boolean;
}

export const QualificationStep = ({ eventId, event, previousStep, nextStep, isActive }: Props) => {
  const { t } = useTranslation();
  const { saveField, isSaving } = useSaveField(eventId);
  const [drawerState, setDrawerState] = useState<DrawerMode | null>(null);
  const [drawerKey, setDrawerKey] = useState(0);
  const confirmationModal = useConfirmationModal();
  const { update: updateIntercom } = useIntercom();

  const items = useMemo(() => event.eventData.selectionCriteria ?? [], [event.eventData.selectionCriteria]);

  useEffect(() => {
    if (!isActive) setDrawerState(null);
  }, [isActive]);

  const isDrawerOpen = drawerState !== null;
  useEffect(() => {
    updateIntercom({ hideDefaultLauncher: isDrawerOpen });
  }, [isDrawerOpen, updateIntercom]);

  const handleSubmit = useCallback(
    async (values: SelectionCriterionDto) => {
      const newList =
        drawerState?.kind === "edit"
          ? items.map((item, i) => (i === drawerState.index ? values : item))
          : [...items, values];
      const saved = await saveField({ selectionCriteria: newList });
      if (saved) setDrawerState(null);
    },
    [items, saveField, drawerState]
  );

  const handleEdit = useCallback(
    (index: number) => {
      const item = items[index];
      setDrawerKey((k) => k + 1);
      setDrawerState({
        kind: "edit",
        index,
        initialValues: {
          type: item.type,
          name: item.name ?? "",
          description: item.description,
          requiresEvidence: item.requiresEvidence,
        },
      });
    },
    [items]
  );

  const handleDelete = useCallback(
    (index: number) => {
      confirmationModal.show({
        title: t("Delete requirement"),
        description: t("Are you sure you want to delete this requirement?"),
        confirmButtonText: t("Delete") ?? "",
        confirmButtonColor: "red",
        onConfirmed: async () => {
          const newList = items.filter((_, i) => i !== index);
          await saveField({ selectionCriteria: newList });
        },
      });
    },
    [items, saveField, confirmationModal, t]
  );

  return (
    <Box pb="8" pt="10">
      <Flex justify="space-between" align="center" mb="6">
        <Heading as="h2" size="md">
          {t("Qualification requirements")} {isSaving("selectionCriteria") && <Spinner size="xs" />}
        </Heading>
        <Button
          colorScheme="teal"
          leftIcon={<AddIcon />}
          onClick={() => { setDrawerKey((k) => k + 1); setDrawerState({ kind: "add" }); }}
        >
          {t("Add requirement")}
        </Button>
      </Flex>

      <Box mb="8">
        <QualificationRequirementList
          items={items}
          onAdd={() => { setDrawerKey((k) => k + 1); setDrawerState({ kind: "add" }); }}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Box>

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={t("Summary")} />

      <QualificationRequirementDrawer
        key={drawerKey}
        isOpen={drawerState !== null}
        isSaving={isSaving("selectionCriteria")}
        mode={drawerState?.kind ?? "add"}
        defaultValues={drawerState?.kind === "edit" ? drawerState.initialValues : undefined}
        onSubmit={handleSubmit}
        onClose={() => setDrawerState(null)}
      />

      {confirmationModal.modal}
    </Box>
  );
};
